import * as Yup from "yup";
import { KeyValueMap } from "../../../redux/slices/appState/models";
import { AddTopicFormKey, topicTypeKey } from "./models";
import { UnitTypesKey } from "../../../helpers/constants/siteCoreKeys";
import {
  EXTENDED_SPECIAL_CHARS,
  INPUT_LIMIT,
} from "../../../helpers/constants";
import { ManageLocales } from "../../createProgramTemplate/modelsAndSchemas/Models";
import {
  checkValueWithValidationRegex,
  specialCharactersNotAllowedValidation,
  specialCharactersNotAllowedValidationForOptionalField,
} from "../../../helpers/constants/commonValidationSchemaUtils";

const localeKeys = ["en-US", "en-GB", "fr-FR", "pt-BR", "ja-JP"] as const;

export type LocaleKey = (typeof localeKeys)[number];

export const AddTopicSchema = (
  commonValidationMessages: KeyValueMap,
  selectedUnit: string,
  mergedLanguages: ManageLocales[],
) => {
  const localeSchema = mergedLanguages.reduce(
    (acc, { key, isMandatory, isEnglish }) => {
      acc[key as LocaleKey] = (
        isMandatory || isEnglish
          ? specialCharactersNotAllowedValidation(
              commonValidationMessages.requiredValidation,
              commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
            )
          : specialCharactersNotAllowedValidationForOptionalField(
              commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
            )
      ) as Yup.StringSchema;
      return acc;
    },
    {} as Record<LocaleKey, Yup.StringSchema>,
  );

  const staticSchema = {
    [AddTopicFormKey.topicType]: Yup.string()
      .oneOf([topicTypeKey.existingTopic, topicTypeKey.newTopic])
      .required(commonValidationMessages.requiredValidation),

    [AddTopicFormKey.topic]: Yup.string()
      .required(commonValidationMessages.requiredValidation)
      .when(AddTopicFormKey.topicType, {
        is: topicTypeKey.newTopic,
        then: (schema) =>
          schema.test(
            EXTENDED_SPECIAL_CHARS,
            commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
            (value) =>
              checkValueWithValidationRegex(
                value,
                INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS,
              ),
          ),
        otherwise: (schema) => schema, // keep only required
      })
      .default(""),
    [AddTopicFormKey.triggerBilling]: Yup.boolean().default(false),

    [AddTopicFormKey.lastContactDate]: Yup.boolean().default(false),

    [AddTopicFormKey.triggerTouch]: Yup.boolean().default(false),
    [AddTopicFormKey.deliverables]: Yup.array().default([]),
    [AddTopicFormKey.activityStatus]: Yup.string()
      .required(commonValidationMessages.requiredValidation)
      .default(""),
    [AddTopicFormKey.participantStatus]: Yup.array()
      .min(1, commonValidationMessages.requiredValidation)
      .default([]),
    [AddTopicFormKey.measurementMinute]: Yup.string()
      .when([], {
        is: () => selectedUnit === UnitTypesKey.Minutes,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
      })
      .default(""),
    [AddTopicFormKey.measurementOther]: Yup.number()
      .nullable()
      .when([], {
        is: () =>
          selectedUnit === UnitTypesKey.Count ||
          selectedUnit === UnitTypesKey.Days,
        then: (schema) =>
          schema
            .required(commonValidationMessages.requiredValidation)
            .typeError(commonValidationMessages.requiredValidation)
            .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
            .max(99, commonValidationMessages.valueRangeFieldValidator1To99),
      })
      .default(0),
    // country field is optional, no required validation here
    [AddTopicFormKey.country]: Yup.string(),
    [AddTopicFormKey.countryMeasurementMinute]: Yup.string().default(""),
    [AddTopicFormKey.countryMeasurementOther]: Yup.number()
      .transform((value, originalValue) => {
        return originalValue === "" ? undefined : value;
      })
      .when([], {
        is: () =>
          selectedUnit === UnitTypesKey.Count ||
          selectedUnit === UnitTypesKey.Days,
        then: (schema) =>
          schema.test({
            name: "minMaxIfPresent",
            message: commonValidationMessages.valueRangeFieldValidator1To99,
            test: (value) => {
              if (value === undefined || value === null || value === 0)
                return true;
              return value >= 1 && value <= 99;
            },
          }),
      })
      .default(0),
  };

  return Yup.object().shape({
    ...staticSchema,
    ...localeSchema,
  });
};
